import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type AccountOpeningCollection, type AccountOpeningStep, type AccountOpeningStepId } from '../../../models/AccountOpeningDescriptor'
import { collectionEntrySteps, flowCollections } from '../domain/collections'
import { CustodianDetailsUnavailable, type PreparedSchemas } from './useCollectionSchemas'

type PreparationFailure = 'custodianDetails' | 'other'

interface CollectionPreparation {
  isHydrating: boolean
  isFilling: boolean
  failed: boolean
  failure: PreparationFailure | null
}

const IDLE: CollectionPreparation = { isHydrating: false, isFilling: false, failed: false, failure: null }

const failedWith = (error: unknown): CollectionPreparation => ({
  isHydrating: false,
  isFilling: false,
  failed: true,
  failure: error instanceof CustodianDetailsUnavailable ? 'custodianDetails' : 'other',
})

interface CollectionPreparations {
  of: (collection: AccountOpeningCollection) => CollectionPreparation
  reset: () => void
  retry: (collection: AccountOpeningCollection) => void
}

export const useCollectionPreparations = ({
  steps,
  reachedStep,
  signatures,
  hydrate,
  fill,
}: {
  steps: AccountOpeningStep[]
  reachedStep: (id: AccountOpeningStepId) => boolean
  signatures: Record<string, string>
  hydrate: (collection: AccountOpeningCollection) => Promise<PreparedSchemas>
  fill: (collection: AccountOpeningCollection, prepared: PreparedSchemas) => Promise<void>
}): CollectionPreparations => {
  const [phases, setPhases] = useState<Record<string, CollectionPreparation>>({})
  const [retries, setRetries] = useState(0)
  const attempted = useRef<Set<string>>(new Set())

  const collections = useMemo(() => flowCollections(steps), [steps])
  const entrySteps = useMemo(() => collectionEntrySteps(steps), [steps])

  const readyKey = collections.map((collection) => (reachedStep(entrySteps.get(collection) ?? '') ? '1' : '0')).join('')

  const setPhase = useCallback((collection: AccountOpeningCollection, phase: CollectionPreparation) => {
    setPhases((previous) => ({ ...previous, [collection]: phase }))
  }, [])

  useEffect(() => {
    collections.forEach((collection) => {
      const signature = signatures[collection] ?? ''
      const entryStep = entrySteps.get(collection)
      if (!signature || !entryStep || !reachedStep(entryStep)) {
        return
      }
      const attempt = `${collection}:${signature}`
      if (attempted.current.has(attempt)) {
        return
      }
      attempted.current.add(attempt)
      setPhase(collection, { ...IDLE, isHydrating: true })
      hydrate(collection)
        .then((prepared) => {
          setPhase(collection, { ...IDLE, isFilling: true })
          return fill(collection, prepared)
        })
        .then(() => setPhase(collection, IDLE))
        .catch((error: unknown) => {
          attempted.current.delete(attempt)
          setPhase(collection, failedWith(error))
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `readyKey` stands in for `reachedStep`, a fresh closure on every render
  }, [collections, entrySteps, signatures, readyKey, hydrate, fill, setPhase, retries])

  const of = useCallback((collection: AccountOpeningCollection) => phases[collection] ?? IDLE, [phases])
  const reset = useCallback(() => {
    attempted.current.clear()
    setPhases({})
  }, [])

  const retry = useCallback(
    (collection: AccountOpeningCollection) => {
      setPhase(collection, IDLE)
      setRetries((count) => count + 1)
    },
    [setPhase],
  )

  return { of, reset, retry }
}
